import { CHAIN_CACHE_TTL_MS } from "config"

interface CacheRecord<T> {
  value: T
  expiresAt: number
}

interface CacheHit<T> {
  hit: true
  value: T
}

interface CacheMiss {
  hit: false
}

interface ChainReadCacheOptions {
  ttlMs?: number
  now?: () => number
}

const CACHE_PREFIX = "r4v3n:chain-read:v1:"
const memoryCache = new Map<string, CacheRecord<unknown>>()
const inflightCache = new Map<string, Promise<unknown>>()

const cacheMiss: CacheMiss = { hit: false }

const getNow = (options: ChainReadCacheOptions) => options.now ?? Date.now

const getBrowserStorage = () => {
  if (typeof window === "undefined") return undefined

  try {
    return window.localStorage
  } catch {
    return undefined
  }
}

const isExpired = (record: CacheRecord<unknown>, now: number) => record.expiresAt <= now

const readMemoryCache = <T>(key: string, now: number): CacheHit<T> | CacheMiss => {
  const record = memoryCache.get(key) as CacheRecord<T> | undefined
  if (!record) return cacheMiss

  if (isExpired(record, now)) {
    memoryCache.delete(key)
    return cacheMiss
  }

  return { hit: true, value: record.value }
}

const readLocalStorageCache = <T>(key: string, now: number): CacheHit<T> | CacheMiss => {
  const storage = getBrowserStorage()
  if (!storage) return cacheMiss

  try {
    const rawRecord = storage.getItem(key)
    if (!rawRecord) return cacheMiss

    const record = JSON.parse(rawRecord) as CacheRecord<T>
    if (!record || typeof record.expiresAt !== "number" || !Object.prototype.hasOwnProperty.call(record, "value")) {
      storage.removeItem(key)
      return cacheMiss
    }

    if (isExpired(record, now)) {
      storage.removeItem(key)
      return cacheMiss
    }

    memoryCache.set(key, record)
    return { hit: true, value: record.value }
  } catch {
    return cacheMiss
  }
}

const writeLocalStorageCache = <T>(key: string, record: CacheRecord<T>) => {
  const storage = getBrowserStorage()
  if (!storage) return

  try {
    storage.setItem(key, JSON.stringify(record))
  } catch {
    // Storage may be unavailable or full; memory cache still protects the session.
  }
}

const writeCache = <T>(key: string, value: T, ttlMs: number, now: number) => {
  const record: CacheRecord<T> = {
    value,
    expiresAt: now + ttlMs,
  }

  memoryCache.set(key, record)
  writeLocalStorageCache(key, record)
}

const normalizeKeyPart = (part: string | number | bigint | boolean) =>
  encodeURIComponent(String(part).trim().toLowerCase())

export const makeChainReadCacheKey = (...parts: Array<string | number | bigint | boolean | null | undefined>) => {
  const keyParts = parts
    .filter((part): part is string | number | bigint | boolean => part !== null && part !== undefined)
    .map(normalizeKeyPart)

  return `${CACHE_PREFIX}${keyParts.join(":")}`
}

export const readThroughChainCache = async <T>(
  key: string,
  fetcher: () => Promise<T>,
  options: ChainReadCacheOptions = {}
): Promise<T> => {
  const ttlMs = options.ttlMs ?? CHAIN_CACHE_TTL_MS
  if (ttlMs <= 0) return fetcher()

  const now = getNow(options)
  const currentTime = now()
  const memoryHit = readMemoryCache<T>(key, currentTime)
  if (memoryHit.hit) return memoryHit.value

  const storageHit = readLocalStorageCache<T>(key, currentTime)
  if (storageHit.hit) return storageHit.value

  const inflightRequest = inflightCache.get(key) as Promise<T> | undefined
  if (inflightRequest) return inflightRequest

  const request = fetcher().then((value) => {
    writeCache(key, value, ttlMs, now())
    return value
  })

  inflightCache.set(key, request)

  try {
    return await request
  } finally {
    if (inflightCache.get(key) === request) {
      inflightCache.delete(key)
    }
  }
}

export const clearChainReadMemoryCache = () => {
  memoryCache.clear()
  inflightCache.clear()
}

export const clearChainReadCache = () => {
  clearChainReadMemoryCache()

  const storage = getBrowserStorage()
  if (!storage) return

  try {
    const keysToRemove: string[] = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (key?.startsWith(CACHE_PREFIX)) keysToRemove.push(key)
    }

    keysToRemove.forEach((key) => storage.removeItem(key))
  } catch {
    // Ignore unavailable storage while still clearing memory state.
  }
}
